import {
  CreateTrainingsTrainingClassAttachmentsInput,
  CreateTrainingsTrainingClassRemindersInput,
  CreateTrainingsTrainingClassSessionRelatorioInput,
  CreateTrainingsTrainingClassTrainingSessionsInput,
  PerformanceTargetStrategySupportedStrategiesEnum,
  SessionClassFragment,
  TrainingsSessionFrequencyEnum,
  TrainingsSessionModalityEnum,
  TrainingsSessionScheduleEnum,
  TrainingsSessionUpdateStrategyEnum,
  TrainingsTrainingClassModalityEnum,
} from 'generated/resources/graphql'
import moment from 'moment'

import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'

import { sumMinutesToHours } from 'modules/trainings/components/MyTrainings/RequestsComponent/RequestDetailComponent/useHoursAndMinutes'

export type SessionData = {
  id?: number
  startsAt?: string
  endsAt?: string
  startsAtHour: string
  endsAtHour: string
  recurrent: boolean
  frequency?: TrainingsSessionFrequencyEnum
  // reminders: used when creating a new session. Creating new
  // reminders when editing an existing sessions saves it
  reminders: CreateTrainingsTrainingClassRemindersInput[]
  duration?: string
  trainingId: number
  trainingClassId?: number
  name: string
  link: string
  hasLearningPlatform?: boolean
  description?: string
  location?: string
  startDate: string
  subsidized?: boolean
  hours?: string
  minutes?: string
  sessionRelatorioUnicoField?: CreateTrainingsTrainingClassSessionRelatorioInput
  modality: TrainingsSessionModalityEnum
  dueDate?: string
  schedule?: TrainingsSessionScheduleEnum | null
  updateStrategy?: TrainingsSessionUpdateStrategyEnum
}

export type ClassWizardData = {
  trainingId: number
  startDate: string
  endDate: string
  companyId: number
  authorId: number
  mode: TrainingsTrainingClassModalityEnum
  name: string
  description?: string
  trainingSessions: CreateTrainingsTrainingClassTrainingSessionsInput[]
  attachments: CreateTrainingsTrainingClassAttachmentsInput[]
  employeeIds: number[]
  cost?: string
  indirectCost?: string
  salaryCost?: string
  subsidizedCost?: string
  codigoGrupo?: string
}

export type EventData = {
  startDate: string
  endDate: string
  startHour: string
  endHour: string
  recurrent: boolean
  frequency?: TrainingsSessionFrequencyEnum
  duration?: number
  description?: string
  modality?: string
  link?: string
  location?: string
  reminders?: ReminderData[]
  employeeData?: EmployeeData[]
}

type EmployeeData = {
  targetStrategy: PerformanceTargetStrategySupportedStrategiesEnum
  employeeIds: number[]
}

export type AttachmentData = {
  filename?: string
  public: boolean
  file: File
}

export type ReminderData = {
  content?: string
  title: string
  scheduledAt: string
  scheduledAtHour: string
}

export const serializeToWizardEventData = (
  event: SessionData
): CreateTrainingsTrainingClassTrainingSessionsInput => {
  return {
    duration: event.duration,
    endsAt: event.endsAt,
    endsAtHour: event.endsAtHour,
    name: event.name,
    recurrent: event.recurrent,
    frequency: event.frequency,
    reminders: JSON.parse(JSON.stringify(event.reminders)),
    trainingId: event.trainingId,
    description: event.description,
    link: event.link,
    startsAt: event.startsAt,
    location: event.location,
    hours: Number(event.hours),
    minutes: Number(event.minutes),
    schedule: event.schedule,
    startDate: event.startDate,
    startsAtHour: event.startsAtHour,
    subsidized: event.subsidized,
    id: event.id,
    sessionRelatorio: event.sessionRelatorioUnicoField,
    modality: event.modality,
    dueDate: event.dueDate,
  }
}

export const deserializeToWizardEventData = (
  event: CreateTrainingsTrainingClassTrainingSessionsInput
): SessionData => ({
  duration: event.duration ?? undefined,
  endsAt: event.endsAt ?? '',
  endsAtHour: event.endsAtHour ?? '',
  name: event.name ?? undefined,
  recurrent: event.recurrent ?? undefined,
  frequency: event.frequency ?? undefined,
  reminders: event.reminders ? JSON.parse(JSON.stringify(event.reminders)) : [],
  trainingId: event.trainingId,
  description: event.description ?? undefined,
  link: event.link ?? '',
  startsAt: event.startsAt ?? '',
  location: event.location ?? undefined,
  startDate: event.startDate ?? '',
  startsAtHour: event.startsAtHour ?? '',
  subsidized: event.subsidized ?? undefined,
  dueDate: event.dueDate ?? undefined,
  hours: String(event.hours),
  minutes: String(event.minutes),
  modality: event.modality ?? 'mixed',
  schedule: event.schedule,
  sessionRelatorioUnicoField: event.sessionRelatorio ?? undefined,
})

// Used when editing a session, to convert from graphql to sessionData
export const sessionDataFromSessionClassFragment = (
  sessionFragment: SessionClassFragment
): SessionData => {
  return {
    ...sessionFragment,
    startsAtHour: '',
    endsAtHour: '',
    recurrent: !!sessionFragment.recurrent,
    frequency: sessionFragment.frequency ?? undefined,
    reminders: [],
    duration: sessionFragment.duration ?? '',
    link: sessionFragment.link ?? '',
    description: sessionFragment.description ?? '',
    location: sessionFragment.location ?? '',
    startDate: sessionFragment.startsAt ? dateBackendDateTimeToUI(sessionFragment.startsAt) : '',
    sessionRelatorioUnicoField: {
      trainingSchedule: sessionFragment?.sessionRelatorioUnicoField?.trainingSchedule ?? '',
    },
    modality: sessionFragment.modality ?? 'mixed',
    dueDate: sessionFragment.dueDate ?? '',
    schedule: sessionFragment.schedule,
  }
}

export const calculateDuration = (
  startHour: string,
  endHour: string
): { hours: number; minutes: number; duration?: string } => {
  const startTime = moment(startHour, 'HH:mm')
  const endTime = moment(endHour, 'HH:mm')
  if (endTime.isBefore(startTime)) {
    return { hours: 0, minutes: 0, duration: undefined }
  }
  const duration = moment.duration(endTime.diff(startTime))
  const hours = duration.hours()
  const minutes = duration.minutes()

  const resDuration = String(sumMinutesToHours(hours, minutes))
  return { hours, minutes, duration: resDuration }
}
